import { EAtajoRangoTiempo } from '@core/enums/atajoRangoTiempo.enum';
import { HttpResponse } from '@angular/common/http';
import { EComandaMesaEstado, EComandaProductoEstado } from '@core/enums/comanda.enum';
import { IProductoData } from '@core/interfaces/entidades/producto.entidad';
import { IProductoCarritoData } from '@core/interfaces/entidades/productoCarrito.entidad';
import { IProductoComprado } from '@core/interfaces/request-response/venta.dto.interface';
import { ComandaItemRes, ProductoComandaReq } from '@core/interfaces/request-response/comanda.dto';
import { environment } from '@env/environment';

export const normalizarTelefono = (telefono: string): string => {
  // Eliminar espacios, guiones y paréntesis
  let numeroNormalizado = telefono.replace(/[\s\-()]/g, '');
  // Asegurarse de que el número comience con el código de país +51
  if (!numeroNormalizado.startsWith('+51')) {
    numeroNormalizado = '+51' + numeroNormalizado;
  }
  return numeroNormalizado;
};

export const primeraLetraMayuscula = (texto: string): string => {
  if (!texto) return texto;
  return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
};

export const buildDiff = <T extends object>(original: T, current: Partial<T>): Partial<T> => {
  return Object.keys(current).reduce((acc, key) => {
    const k = key as keyof T;
    if (original[k] !== current[k]) {
      acc[k] = current[k];
    }
    return acc;
  }, {} as Partial<T>);
};

/**
 * Redondea a 2 decimales de forma segura
 */
export function redondearDosDecimales(valor: number): number {
  return Math.round((Number(valor) + Number.EPSILON) * 100) / 100;
}

export const calcularAtajoRangoFecha = (tiempo: EAtajoRangoTiempo): { inicio: Date; fin: Date } => {
  const hoy = new Date();
  const y = hoy.getFullYear();
  const m = hoy.getMonth();
  const d = hoy.getDate();
  let inicio: Date;
  let fin: Date;
  switch (tiempo) {
    case EAtajoRangoTiempo.ULTIMOS_7_DIAS: {
      const i = new Date(y, m, d - 7, 0, 0, 0, 0);
      inicio = i;
      fin = new Date(y, m, d, 23, 59, 59, 999);
      break;
    }
    case EAtajoRangoTiempo.ESTE_MES:
      inicio = new Date(y, m, 1, 0, 0, 0, 0);
      fin = new Date(y, m, d, 23, 59, 59, 999);
      break;
    case EAtajoRangoTiempo.MES_PASADO:
      inicio = new Date(y, m - 1, 1, 0, 0, 0, 0);
      fin = new Date(y, m, 0, 23, 59, 59, 999);
      break;
    case EAtajoRangoTiempo.HOY:
      inicio = new Date(y, m, d, 0, 0, 0, 0);
      fin = new Date(y, m, d, 23, 59, 59, 999);
      break;
    default:
      inicio = new Date(y, m, d, 0, 0, 0, 0);
      fin = new Date(y, m, d, 23, 59, 59, 999);
      break;
  }
  return { inicio, fin };
};
export const estadoProductoEnumToString = (productoEstado: EComandaProductoEstado): string => {
  switch (productoEstado) {
    case EComandaProductoEstado.PENDIENTE:
      return 'Pendiente';
    case EComandaProductoEstado.EN_PREPARACION:
      return 'Preparando';
    case EComandaProductoEstado.LISTO:
      return 'Listo';
    case EComandaProductoEstado.ENTREGADO:
      return 'Entregado';
    case EComandaProductoEstado.CANCELADO:
      return 'Cancelado';
    default:
      return '';
  }
};
export const estadoComandaMesaEnumToString = (mesaEstado: EComandaMesaEstado): string => {
  switch (mesaEstado) {
    case EComandaMesaEstado.LIBRE:
      return 'Libre';
    case EComandaMesaEstado.OCUPADA:
      return 'Ocupada';
    case EComandaMesaEstado.RESERVADA:
      return 'Reservada';
    default:
      return '';
  }
};

export const buildProductoToCarritoCompra = (
  producto: IProductoData,
  cantidad = 1,
): IProductoCarritoData => {
  return {
    id: producto.id,
    codigo: producto.codigo,
    nombre: producto.nombre,
    stock: producto.stock_sucursal.stock,
    controlar_stock: producto.stock_sucursal.controlar_stock,
    cantidad,
    precio: producto.precio_venta_final ?? producto.precio_venta,
    producto_servicio: producto.producto_servicio,
    nota: undefined,
    descuento_campana_id: producto.descuento_campana_id ?? null,
    descuento_objetivo_tipo: producto.descuento_objetivo_tipo ?? null,
    descuento_valor_aplicado: producto.descuento_valor_aplicado ?? 0,
  };
};

/**
 * Convierte una línea del carrito al payload de venta que espera el backend.
 * Los datos de campaña forman parte del contrato de `IProductoComprado`; si no
 * se envían, el backend guarda los descuentos sin trazabilidad.
 */
export const buildProductoCarritoToProductoComprado = (
  producto: IProductoCarritoData,
): IProductoComprado => {
  return {
    id: producto.id,
    nombre: producto.nombre,
    codigo: producto.codigo,
    cantidad: producto.cantidad,
    precio_unitario: producto.precio,
    descuento_campana_id: producto.descuento_campana_id ?? undefined,
    descuento_objetivo_tipo: producto.descuento_objetivo_tipo ?? undefined,
    descuento_valor_aplicado: producto.descuento_valor_aplicado ?? 0,
  };
};
export const buildProductoCarritoToProductoComandaRq = (
  producto: IProductoCarritoData,
): ProductoComandaReq => {
  return {
    producto_id: producto.id ?? undefined,
    codigo: producto.codigo ?? undefined,
    nombre_producto: producto.nombre,
    precio_unitario: producto.precio,
    cantidad: producto.cantidad,
    nota: producto.nota ?? undefined,
  };
};
export const buildProductoComandaToProductoCarrito = (
  comanda: ComandaItemRes,
): IProductoCarritoData => {
  return {
    id: comanda.producto_id,
    codigo: comanda.codigo,
    nombre: comanda.nombre_producto,
    stock: 0,
    controlar_stock: false,
    cantidad: comanda.cantidad,
    precio: comanda.precio_unitario,
    nota: comanda.nota || undefined,
  };
};
export const estadoGeneralMesaPorProductos = (productos: ComandaItemRes[]): EComandaMesaEstado => {
  if (productos.length === 0) {
    return EComandaMesaEstado.LIBRE;
  }
  const estados = productos.map((p) => p.estado);
  if (estados.every((e) => e === EComandaProductoEstado.CANCELADO)) {
    return EComandaMesaEstado.LIBRE;
  }
  return EComandaMesaEstado.OCUPADA;
};

export const logger = (message?: any, ...optionalParams: any[]) => {
  if (environment.name === 'production') {
    return;
  }
  if (message) {
    console.log(`[${new Date().toISOString()}]`, message, ...optionalParams);
  } else {
    console.log(`[${new Date().toISOString()}]`, ...optionalParams);
  }
};

export const descargarArchivoDesdeResponse = (
  response: HttpResponse<Blob>,
  defaultFilename: string,
) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const contentDisposition = response.headers.get('content-disposition') || '';
  const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  const filename = filenameStarMatch?.[1]
    ? decodeURIComponent(filenameStarMatch[1])
    : filenameMatch?.[1] || defaultFilename;

  const blob = response.body;
  if (!blob) return;

  const downloadUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(downloadUrl);
};

export const formatearErrorStock = (mensaje: string): string => {
  const match = mensaje.match(
    /Stock insuficiente de '(.+)' para preparar (\d+) '(.+)'\. Disponible: ([\d.]+)(\w+), Requerido: ([\d.]+)(\w+)/,
  );
  if (!match) return mensaje;

  const [, ingrediente, cantidad, plato, disponible, unidadDisp, requerido, unidadReq] = match;

  return `No hay suficiente <strong>${ingrediente}</strong> para preparar ${cantidad} <strong>${plato}</strong>.<br><br>Disponible: ${disponible} ${unidadDisp}<br>Requerido: ${requerido} ${unidadReq}`;
};
